import { useState, useCallback, useRef } from 'react';
import { PAGE_SIZE } from '../../../../constants/selectFilter';
import { executeApiAction } from '../../../../utility/apiHelper';

export function useLazyOptions(fetchOptionsFn, dispatch) {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const offsetRef = useRef(0);
  const searchTermRef = useRef('');
  const isLoadingRef = useRef(false);

  const loadOptions = useCallback(
    async (searchTerm, offset, shouldReset) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;

      await executeApiAction({
        dispatch,
        setLoading: setIsLoading,
        action: () => fetchOptionsFn({ search: searchTerm, offset, limit: PAGE_SIZE }),
        onSuccess: (result) => {
          setOptions((prevOptions) =>
            shouldReset ? result.items : [...prevOptions, ...result.items]
          );
          offsetRef.current = offset + result.items.length;
          setHasMore(offset + result.items.length < result.totalCount);
        },
        onFinally: () => {
          isLoadingRef.current = false;
        },
      });
    },
    [fetchOptionsFn, dispatch]
  );

  const handleOpen = useCallback(() => {
    if (options.length === 0 && !isLoadingRef.current) {
      offsetRef.current = 0;
      loadOptions('', 0, true);
    }
  }, [options.length, loadOptions]);

  const handleSearch = useCallback(
    (searchTerm) => {
      searchTermRef.current = searchTerm;
      offsetRef.current = 0;
      setHasMore(true);
      loadOptions(searchTerm, 0, true);
    },
    [loadOptions]
  );

  const handleScrollToBottom = useCallback(() => {
    if (hasMore && !isLoadingRef.current) {
      loadOptions(searchTermRef.current, offsetRef.current, false);
    }
  }, [hasMore, loadOptions]);

  const handleClose = useCallback(() => {
    setOptions([]);
    setHasMore(true);
    offsetRef.current = 0;
    searchTermRef.current = '';
  }, []);

  return {
    options,
    loading: isLoading,
    hasMore,
    onOpen: handleOpen,
    onSearch: handleSearch,
    onScrollBottom: handleScrollToBottom,
    onClose: handleClose,
  };
}
